import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import jsPDF from "jspdf";
import { storage } from "../firebase";

const FUNCTIONS_BASE = "https://us-central1-edifice-view.cloudfunctions.net";

export async function uploadWorkOrderSignature(workOrderId, crewId, blob) {
  const path = `workOrders/${workOrderId}/signatures/${crewId}_${Date.now()}.png`;
  const r = ref(storage, path);
  await uploadBytes(r, blob);
  return getDownloadURL(r);
}

// Real fetch()-of-pixel-bytes, needed so jsPDF can embed the signature
// image rather than just link to it. CustodialView hit a real production
// bug here: every buyer's Storage bucket ships with zero CORS
// configuration by default, so this exact fetch() throws "Failed to
// fetch" in the browser (a plain <img> tag never needs CORS, which is why
// every other photo feature in an app like this can work fine while this
// one call fails) — see that repo's memory notes on the fix
// (`setBucketCors`, origin:["*"], methods:["GET","HEAD"]). Not yet
// confirmed either way for edifice-view's own bucket as of this build —
// if a generated PDF is missing its signature image, check the bucket's
// CORS config before assuming this code is broken.
async function urlToDataUrl(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// One-page PDF: title/description, facility + assignee, then each
// assigned crew member's name, the site contact who actually signed, and
// the signature image itself. Ported from CustodialView's
// buildWorkOrderPdf — same shape, adapted field names (siteContactName
// instead of clientName, assigneeType/vendorName instead of an implicit
// single client).
export async function buildWorkOrderPdf(wo, facilityName) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const margin = 48;
  let y = margin;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(wo.title, margin, y); y += 26;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(90);
  doc.text(`${facilityName}${wo.vendorName ? " · " + wo.vendorName : " · Internal Staff"}`, margin, y); y += 18;
  doc.text(`Created ${wo.createdAtLabel || ""}`, margin, y); y += 24;
  doc.setTextColor(20);

  if (wo.description) {
    const lines = doc.splitTextToSize(wo.description, 515);
    doc.text(lines, margin, y);
    y += lines.length * 14 + 16;
  }

  doc.setDrawColor(210);
  doc.line(margin, y, 563, y); y += 24;

  for (const crew of wo.assignedCrew || []) {
    const sig = (wo.signatures || []).find((s) => s.crewId === crew.id);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(crew.name, margin, y); y += 16;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(90);
    if (sig) {
      doc.text(`Signed by ${sig.siteContactName || "site contact"} — ${sig.signedAtLabel || ""}`, margin, y); y += 14;
      try {
        const dataUrl = await urlToDataUrl(sig.signatureUrl);
        doc.addImage(dataUrl, "PNG", margin, y, 200, 62);
        y += 74;
      } catch (e) {
        doc.text("(signature image could not be loaded)", margin, y); y += 16;
      }
    } else {
      doc.text("Awaiting signature", margin, y); y += 14;
    }
    doc.setTextColor(20);
    y += 10;
  }

  return doc;
}

export function sendWorkOrderEmail({ facilityId, workOrderId, to, pdfBase64, fileName, subject }) {
  return fetch(`${FUNCTIONS_BASE}/sendWorkOrderEmail`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ facilityId, workOrderId, to, pdfBase64, fileName, subject }),
  }).then(async (res) => {
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Email failed");
    return data;
  });
}

export function sendWorkOrderText({ facilityId, to, pdfUrl, title }) {
  return fetch(`${FUNCTIONS_BASE}/sendWorkOrderText`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ facilityId, to, pdfUrl, title }),
  }).then(async (res) => {
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Text failed");
    return data;
  });
}
